import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * Bootstrap-only signup page. After the very first Super Admin exists,
 * additional internal users must be created by a Super Admin from
 * Settings / Admin → Create internal user.
 */
export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    api.get("/auth/bootstrap-status").then(r => {
      setAllowed(!!r.data?.needs_bootstrap);
    }).catch(() => setAllowed(false)).finally(()=>setChecking(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await signup({ name, email, password, role: "super_admin" });
      toast.success("Initial Super Admin created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Sign up failed"));
    } finally { setBusy(false); }
  }

  if (checking) return null;
  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper px-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full grid place-items-center" style={{background:"hsl(40 48% 92%)"}}>
              <Lock className="w-5 h-5" style={{color:"hsl(347 72% 22%)"}} />
            </div>
            <div className="font-display text-2xl text-foreground mt-3">Internal access only</div>
            <p className="text-sm text-muted-foreground mt-2">Kandake Prime does not allow public sign-up. New internal users must be created by a Super Admin from Settings / Admin.</p>
            <Link to="/login"><Button className="mt-4" style={{background:"hsl(347 72% 22%)"}}>Go to sign in</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="brand-strip text-white p-10 md:p-14 hidden md:flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: "hsl(347 72% 14%)" }}>
            <Shield className="w-5 h-5" style={{ color: "hsl(40 48% 70%)" }} />
          </div>
          <div>
            <div className="font-display text-2xl leading-none">Kandake Prime</div>
            <div className="text-[11px] uppercase tracking-[0.18em] opacity-80 mt-1">GFMP Command Center · Internal Alpha</div>
          </div>
        </div>
        <div>
          <p className="font-display text-3xl leading-tight max-w-md">First-time setup.</p>
          <p className="text-sm opacity-80 mt-4 max-w-md">The first account created is automatically promoted to Super Admin. Subsequent internal users must be added from Settings / Admin.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-paper">
        <Card className="w-full max-w-md shadow-sm">
          <CardContent className="p-8">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium" style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(40 48% 45%)" }} /> Internal Alpha · Bootstrap
            </span>
            <h1 className="font-display text-3xl text-foreground mt-3">Create initial Super Admin</h1>
            <p className="text-sm text-muted-foreground mt-1">GFMP / Durban 49 internal access only.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={name} onChange={(e)=>setName(e.target.value)} required data-testid="signup-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="signup-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password (≥6 chars)</Label>
                <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={6} data-testid="signup-password-input" />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="signup-submit-button" style={{background:"hsl(347 72% 22%)"}}>{busy ? "Creating…" : "Create Super Admin"}</Button>
            </form>
            <div className="text-sm text-muted-foreground mt-6 text-center">
              Already have an account? <Link to="/login" className="text-foreground underline">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
